/**
 * O modo demonstração: um evento de exemplo, dentro do aparelho, para quem
 * precisa ver o app funcionando sem ter portaria nem servidor.
 *
 * Existe por causa da revisão da Google Play. O revisor precisa passar do login
 * e chegar a um APROVADO; sem isso ele reprova por "não consegui acessar o
 * app". Depender de um evento de homologação no servidor de cada ticketeira
 * significava, para catorze apps, catorze eventos vivos e códigos que se
 * consomem a cada revisão — na quinta atualização, acabam.
 *
 * NÃO é comportamento diferente para o revisor, e a diferença importa: o Google
 * proíbe app que se comporta de um jeito para quem revisa e de outro para o
 * público. Aqui é uma conta de demonstração que qualquer pessoa com a
 * credencial usa e vê a mesma coisa, declarada na ficha da loja e rotulada na
 * tela — o nome do evento diz "DEMONSTRAÇÃO" e aparece no cabeçalho de todas as
 * telas depois do login.
 *
 * Os dados passam pela MESMA cadeia de regras da portaria de verdade. Nada de
 * tela falsa: `validarIngresso` recebe o repositório por parâmetro, então o
 * evento semeado no banco local percorre dígito verificador, lote, setor,
 * reentrada — tudo. É o app inteiro, sobre dados de exemplo.
 */

/**
 * Credencial da conta de demonstração.
 *
 * Não é segredo, e não faz sentido tratá-la como tal: ela é publicada no campo
 * "Acesso ao app" do Play Console, visível para o time de revisão e para quem
 * tiver acesso ao console. O que ela abre é um evento de exemplo no próprio
 * aparelho — extrair do APK não dá acesso a dado de ninguém.
 *
 * Hoje este par NÃO existe no servidor das ticketeiras, e é por isso que o
 * login o reconhece antes de chamar a rede. Se um dia ele for criado lá, tire
 * daqui: credencial que existe nos dois lugares vira duas verdades, e a do
 * aparelho ganharia em silêncio.
 */
export const OPERADOR_DEMONSTRACAO = {
  usuarioId: '1208',
  pin: '2026',
  nome: 'Demonstração',
} as const;

/**
 * O evento de exemplo usa id NEGATIVO, e isso não é enfeite.
 *
 * Evento de verdade sempre chega com `remoteId` positivo do servidor, então o
 * sinal separa os dois sem coluna nova — e a regra da casa diz que migração só
 * adiciona. É por esse sinal que o agendador sabe não enviar validação de
 * demonstração ao servidor: uma entrada de exemplo contada como pública
 * estragaria o número de um evento real, e ninguém desconfiaria da origem.
 */
export const EVENTO_DEMONSTRACAO = -1;

/** Setor do evento de exemplo — negativo pelo mesmo motivo do evento. */
export const SETOR_DEMONSTRACAO = -1;

/**
 * Nome do evento de exemplo. Aparece no cabeçalho das telas de setores e do
 * validador — e, por isso, nas capturas de tela da ficha da loja, que saem
 * deste mesmo fluxo.
 *
 * Já se chamou "DEMONSTRAÇÃO — dados de exemplo", para rotular o modo na
 * própria tela. Trocado por um nome apresentável depois de decidir que a ficha
 * da loja não deve anunciar "dados de exemplo" em quase toda imagem.
 *
 * A conformidade não depende deste texto: quem declara que é conta de
 * demonstração é o campo "Acesso ao app" do Play Console, que é o canal
 * oficial para isso e está gerado em play/<id>/acesso-ao-app.md. O "Exemplo"
 * no nome fica como lembrete honesto para quem estiver com o app na mão.
 */
export const NOME_EVENTO_DEMONSTRACAO = 'Festival de Exemplo';

/**
 * Faixa de lote do evento de exemplo, no formato que o TMobi grava:
 * `"92026 - 92026"`. `candidatosDeLote` de um código `92026…` devolve
 * `['92026', '2026']`, e o primeiro casa com este lote por LIKE.
 *
 * Cinco dígitos, e não quatro, para reduzir a chance de colidir com o lote de
 * alguma ticketeira — `loteStr` é único no banco inteiro, não por evento.
 */
export const LOTE_DEMONSTRACAO = '92026 - 92026';

/**
 * Ingressos de exemplo. Treze dígitos com verificador EAN-13 correto, porque a
 * regra 1 de `validarIngresso` recusa o resto — código inventado sem
 * verificador daria CODIGO_INVALIDO e a demonstração morreria no primeiro
 * passo.
 *
 * Seis, e não um: cada leitura consome um, e quem estiver demonstrando o app
 * quer poder repetir sem reinstalar.
 */
export const CODIGOS_DEMONSTRACAO = [
  '9202600000010',
  '9202600000027',
  '9202600000034',
  '9202600000041',
  '9202600000058',
  '9202600000065',
] as const;

/** Nomes para os ingressos nominais do exemplo, que alimentam a busca por portador. */
export const PORTADORES_DEMONSTRACAO = [
  {codbar: '9202600000010', nome: 'Ana Souza', cpf: '00000000191'},
  {codbar: '9202600000027', nome: 'Bruno Lima', cpf: '00000000272'},
] as const;

/** A credencial digitada é a da conta de demonstração? */
export function ehDemonstracao(usuarioId: string, pin: string): boolean {
  return (
    usuarioId.trim() === OPERADOR_DEMONSTRACAO.usuarioId &&
    pin.trim() === OPERADOR_DEMONSTRACAO.pin
  );
}

/**
 * Este evento é o de demonstração?
 *
 * Pergunte por aqui, e não comparando com a constante: o que define é o sinal,
 * e quem ler isto no futuro precisa ver o critério e não um número solto.
 */
export function ehEventoDeDemonstracao(eventoId: number): boolean {
  return Number(eventoId) < 0;
}
